import styled from '@emotion/styled';
import useRegionList from 'components/StoreSelector/hooks/useRegionList';
import { useStoreInfoSelect } from '../ImplementData/components/Filter/Provider';
import { ReactComponent as IconSvg1 } from './assets/icon1.svg';
import { ReactComponent as IconSvg2 } from './assets/icon2.svg';
import { ReactComponent as IconSvg3 } from './assets/icon3.svg';
import { ReactComponent as IconSvg4 } from './assets/icon4.svg';
import { ReactComponent as IconSvg5 } from './assets/icon5.svg';
import { ReactComponent as IconSvg6 } from './assets/icon6.svg';
import { useRequest } from 'ahooks';
import { getDepartmentList } from 'services/api/dashboard';
import { useEffect, useRef, useState } from 'react';
import { FilterStoreInfo } from '../ImplementData/components/Filter/useFilter';
import dayjs, { Dayjs } from 'dayjs';
import ExtraNode from '../ImplementData/components/Filter/ExtraNode';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { formatNumber } from 'utils/numberFormat';
import { Badge, message, Progress, Table, Tooltip } from 'antd';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { getMercadoQaDashboardData } from 'views/Mercado/FAQ/services';
import { getLocalTimeZone } from 'utils/timeZone';
import { getChartMaxValue } from '../Activity/useActivityContents';
import { applyChartTheme, initEcharts, echarts } from 'lib/echarts';
import { useTranslate } from '@tolgee/react';

function QaReply() {
  const { t } = useTranslate();

  const { data: { departments } = {} } = useRequest(() => getDepartmentList('order'));

  const regionInfo = useRegionList({
    scene: 'ORDER_STAT',
  });

  const [filter, setFilter] = useState<{
    storeInfo: FilterStoreInfo;
    dateRange: [Dayjs, Dayjs];
  }>({
    storeInfo: {},
    dateRange: [
      dayjs().subtract(7, 'days').startOf('days'),
      dayjs().subtract(1, 'days').endOf('days'),
    ],
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    scene: 'ORDER_STAT',
    regionInfo,
    platform: 'MERCADO',
    platformOptions: [{ value: 'MERCADO', label: 'Mercado' }],
    departments,
    storeInfo: filter.storeInfo,
    onChange: (storeInfo) => setFilter({ ...filter, storeInfo }),
  });

  const rangePick = <ExtraNode value={filter} onChange={setFilter} showQuickActions />;

  const { loading, data } = useRequest(
    () => {
      return getMercadoQaDashboardData({
        storeIds: filter.storeInfo.list?.flat().map((_) => Number(_.id)) || [],
        startDate: filter.dateRange[0].format('YYYY-MM-DD'),
        endDate: filter.dateRange[1].format('YYYY-MM-DD'),
        timeZone: getLocalTimeZone(),
      }).catch(() => {
        return {
          summary: {
            totalCount: '100',
            repliedCount: '80',
            unrepliedCount: '20',
            replyRatio: 0.8,
            unrepliedRatio: 0.2,
          },
          dailyMetrics: new Array(15).fill('').map((_, i) => {
            return {
              date: `2026-09-${i}`,
              totalCount: '20',
              agentReply: {
                count: Math.random() * 100000,
                ratio: Math.random(),
              },
              aiAutoReply: {
                count: Math.random() * 100000,
                ratio: Math.random(),
              },
              externalPlatformReply: {
                count: Math.random() * 100000,
                ratio: Math.random(),
              },
              unreplied: {
                count: Math.random() * 100000,
                ratio: Math.random(),
              },
            };
          }),
          agentRankings: [
            {
              userId: '12',
              userName: '客服A',
              count: '30',
              ratio: 0.3,
            },
            {
              userId: '12',
              userName: '客服B',
              count: '20',
              ratio: 0.2,
            },
            {
              userId: '12',
              userName: '客服C',
              count: '20',
              ratio: 0.2,
            },
            {
              userId: '12',
              userName: '客服D',
              count: '10',
              ratio: 0.1,
            },
            {
              userId: '12',
              userName: '客服D',
              count: '10',
              ratio: 0.1,
            },
            {
              userId: '12',
              userName: '客服D',
              count: '10',
              ratio: 0.1,
            },
          ],
          agentReplyMethods: {
            manual: {
              count: '25',
              ratio: 0.5,
            },
            aiDirect: {
              count: '15',
              ratio: 0.3,
            },
            aiEdited: {
              count: '10',
              ratio: 0.2,
            },
          },
        };
      });
    },
    {
      refreshDeps: [filter],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const chartDomRef1 = useRef<HTMLDivElement>(null);
  const chartDomRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartDomRef1.current || !chartDomRef2.current || !data) {
      return;
    }

    const { dailyMetrics } = data;

    const dateList = dailyMetrics.map((_) => _.date);

    const agentReplyCountList = dailyMetrics.map((_) => Number(_.agentReply.count));
    const aiAutoReplyCountList = dailyMetrics.map((_) => Number(_.aiAutoReply.count));
    const externalPlatformReplyCountList = dailyMetrics.map((_) =>
      Number(_.externalPlatformReply.count),
    );
    const unrepliedCountList = dailyMetrics.map((_) => Number(_.unreplied.count));

    const agentReplyRatioList = dailyMetrics.map((_) => _.agentReply.ratio * 100);
    const aiAutoReplyRatioList = dailyMetrics.map((_) => _.aiAutoReply.ratio * 100);
    const externalPlatformReplyRatioList = dailyMetrics.map(
      (_) => _.externalPlatformReply.ratio * 100,
    );
    const unrepliedRatioList = dailyMetrics.map((_) => _.unreplied.ratio * 100);

    const max = getChartMaxValue(
      [
        ...agentReplyCountList,
        ...aiAutoReplyCountList,
        ...externalPlatformReplyCountList,
        ...unrepliedCountList,
      ],
      5,
    );

    const chart1 = initEcharts(chartDomRef1.current);
    const chart2 = initEcharts(chartDomRef2.current);

    chart1.setOption(
      applyChartTheme({
        tooltip: {
          trigger: 'axis',
        },
        legend: {
          show: false,
        },
        grid: {
          left: '1%',
          right: '2%',
          top: '2%',
          bottom: '1%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: dateList,
        },
        yAxis: [
          {
            type: 'value',
            min: 0,
            max: max,
            interval: max / 5,
          },
        ],
        series: [
          {
            name: t('客服回复'),
            type: 'line',
            data: agentReplyCountList,
            smooth: true,
            showSymbol: false,
            itemStyle: {
              color: '#73A0FA',
            },
            areaStyle: {
              opacity: 0.2,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: '#73A0FA',
                },
                {
                  offset: 1,
                  color: '#ffffff00',
                },
              ]),
            },
          },
          {
            name: t('AI 自动回答'),
            type: 'line',
            data: aiAutoReplyCountList,
            smooth: true,
            showSymbol: false,
            itemStyle: {
              color: '#34C78B',
            },
            areaStyle: {
              opacity: 0.2,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: '#34C78B',
                },
                {
                  offset: 1,
                  color: '#ffffff00',
                },
              ]),
            },
          },
          {
            name: t('其他平台'),
            type: 'line',
            data: externalPlatformReplyCountList,
            smooth: true,
            showSymbol: false,
            itemStyle: {
              color: '#A494FA',
            },
            areaStyle: {
              opacity: 0.2,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: '#A494FA',
                },
                {
                  offset: 1,
                  color: '#ffffff00',
                },
              ]),
            },
          },
          {
            name: t('unanswered'),
            type: 'line',
            data: unrepliedCountList,
            smooth: true,
            showSymbol: false,
            itemStyle: {
              color: '#A0A5B0',
            },
            areaStyle: {
              opacity: 0.2,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: '#A0A5B0',
                },
                {
                  offset: 1,
                  color: '#ffffff00',
                },
              ]),
            },
          },
        ],
      }),
    );

    chart2.setOption(
      applyChartTheme({
        tooltip: {
          trigger: 'axis',
          valueFormatter: (value: any) => formatNumber(value / 100, '0.0%'),
        },
        legend: {
          show: false,
        },
        grid: {
          left: '1%',
          right: '2%',
          top: '2%',
          bottom: '1%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: dateList,
        },
        yAxis: [
          {
            type: 'value',
            min: 0,
            max: 100,
            interval: 20,
            axisLabel: {
              formatter: '{value}%',
            },
          },
        ],
        series: [
          {
            name: t('客服回复'),
            type: 'bar',
            stack: 'total',
            data: agentReplyRatioList,
            barMaxWidth: 18,
            itemStyle: {
              color: '#73A0FA',
              borderRadius: [0, 0, 6, 6],
            },
          },
          {
            name: t('AI 自动回答'),
            type: 'bar',
            stack: 'total',
            data: aiAutoReplyRatioList.map((value, index) => {
              const isFirst =
                unrepliedRatioList[index] === 0 && externalPlatformReplyRatioList[index] === 0;
              const isLast = agentReplyRatioList[index] === 0;
              const topBorder = isFirst ? 6 : 0;
              const bottomBorder = isLast ? 6 : 0;
              return {
                value,
                itemStyle: {
                  borderRadius: [topBorder, topBorder, bottomBorder, bottomBorder],
                },
              };
            }),
            barMaxWidth: 18,
            itemStyle: {
              color: '#34C78B',
            },
          },
          {
            name: t('其他平台'),
            type: 'bar',
            stack: 'total',
            data: externalPlatformReplyRatioList.map((value, index) => {
              const isFirst = unrepliedRatioList[index] === 0;
              const isLast = aiAutoReplyRatioList[index] === 0 && agentReplyRatioList[index] === 0;
              const topBorder = isFirst ? 6 : 0;
              const bottomBorder = isLast ? 6 : 0;
              return {
                value,
                itemStyle: {
                  borderRadius: [topBorder, topBorder, bottomBorder, bottomBorder],
                },
              };
            }),
            barMaxWidth: 18,
            itemStyle: {
              color: '#A494FA',
            },
          },
          {
            name: t('unanswered'),
            type: 'bar',
            stack: 'total',
            data: unrepliedRatioList,
            barMaxWidth: 18,
            itemStyle: {
              color: '#A0A5B0',
              borderRadius: [6, 6, 0, 0],
            },
          },
        ],
      }),
    );

    const handler = () => {
      chart1.resize();
      chart2.resize();
    };

    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);

      chart1.dispose();
      chart2.dispose();
    };
  }, [data, t]);

  if (!data) {
    return (
      <Container>
        <header>
          {storeInfoSelect}
          {rangePick}
        </header>
        {loading && <LoadingOutlined />}
      </Container>
    );
  }

  const { summary, agentRankings, agentReplyMethods } = data;

  const lenged = (
    <ul>
      <li>
        <Badge color="#73A0FA" text={t('客服回复')} />
        <Tooltip title={t('客服回复tips')}>
          <What />
        </Tooltip>
      </li>
      <li>
        <Badge color="#34C78B" text={t('AI 自动回答')} />
      </li>
      <li>
        <Badge color="#A494FA" text={t('其他平台')} />
      </li>
      <li>
        <Badge color="#A0A5B0" text={t('未回复')} />
      </li>
    </ul>
  );

  return (
    <Container>
      <header>
        {storeInfoSelect}
        {rangePick}
      </header>
      <ul>
        <li>
          <Icon component={IconSvg1} />
          <div>
            <p>{t('Q&A总数')}</p>
            <b>{formatNumber(summary.totalCount, '0,0')}</b>
          </div>
        </li>
        <li>
          <Icon component={IconSvg2} />
          <div>
            <p>
              {t('answered')}
              <Tooltip title={t('包括客服回复、AI 自动回复及其他平台回复。')}>
                <What />
              </Tooltip>
            </p>
            <b>{formatNumber(summary.repliedCount, '0,0')}</b>
            <span>{`${t('回复率')} ${formatNumber(summary.replyRatio, '0.0%')}`}</span>
          </div>
        </li>
        <li>
          <Icon component={IconSvg3} />
          <div>
            <p>{t('unanswered')}</p>
            <b>{formatNumber(summary.unrepliedCount, '0,0')}</b>
            <span>{`${t('占总数')} ${formatNumber(summary.unrepliedRatio, '0.0%')}`}</span>
          </div>
        </li>
      </ul>
      <div>
        <header>
          <h3>{t('Q&A回复量')}</h3>
        </header>
        <div>
          {lenged}
          <section ref={chartDomRef1} />
        </div>
      </div>
      <div>
        <header>
          <h3>{t('Q&A回复占比')}</h3>
        </header>
        <div>
          {lenged}
          <section ref={chartDomRef2} />
        </div>
      </div>
      <section>
        <div>
          <header>
            <h3>{t('客服回复量')}</h3>
          </header>
          <div>
            <Table
              dataSource={agentRankings}
              columns={[
                {
                  key: 'index',
                  title: t('排名'),
                  width: 80,
                  render: (_, __, index) => index + 1,
                },
                {
                  key: 'userName',
                  dataIndex: 'userName',
                  title: t('refundSetting.客服昵称'),
                  width: 180,
                },
                {
                  key: 'count',
                  dataIndex: 'count',
                  title: t('数量'),
                  width: 80,
                  render: (count: string) => formatNumber(count, '0,0'),
                },
                {
                  key: 'ratio',
                  dataIndex: 'ratio',
                  title: (
                    <div>
                      {t('占比')}
                      <Tooltip title={t('占比tips')}>
                        <What />
                      </Tooltip>
                    </div>
                  ),
                  width: 180,
                  align: 'right',
                  render: (ratio: number) => (
                    <Progress percent={Number((ratio * 100).toFixed(1))} />
                  ),
                },
              ]}
              pagination={false}
            />
          </div>
        </div>
        <div>
          <header>
            <h3>{t('客服回复方式')}</h3>
          </header>
          <div>
            <ul>
              <li>
                <Icon component={IconSvg4} />
                <div>
                  <span>{t('手动回复')}</span>
                  <p>
                    <b>{formatNumber(agentReplyMethods.manual.count, '0,0')}</b>
                    {formatNumber(agentReplyMethods.manual.ratio, '0.0%')}
                  </p>
                </div>
              </li>
              <li>
                <Icon component={IconSvg5} />
                <div>
                  <span>{t('直接采用 AI 推荐回复')}</span>
                  <p>
                    <b>{formatNumber(agentReplyMethods.aiDirect.count, '0,0')}</b>
                    {formatNumber(agentReplyMethods.aiDirect.ratio, '0.0%')}
                  </p>
                </div>
              </li>
              <li>
                <Icon component={IconSvg6} />
                <div>
                  <span>{t('调整后，采用 AI 推荐回复')}</span>
                  <p>
                    <b>{formatNumber(agentReplyMethods.aiEdited.count, '0,0')}</b>
                    {formatNumber(agentReplyMethods.aiEdited.ratio, '0.0%')}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </Container>
  );
}

const Container = styled.div`
  height: 100%;
  overflow: auto;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
    height: 45px;
    padding: 0 16px;
    margin-bottom: 14px;
    background: var(--fett-bg-panel);
  }
  > ul {
    display: flex;
    align-items: center;
    gap: 14px;
    list-style: none;
    margin: 0;
    padding: 0;
    margin-bottom: 14px;
    > li {
      flex: 1 0 0;
      display: flex;
      height: 94px;
      padding: 14px;
      background: var(--fett-bg-panel);
      border-radius: 8px;
      > .anticon {
        flex: 0 0 auto;
        margin-right: 14px;
        font-size: 48px;
      }
      > div {
        flex: 1 0 0;
        display: flex;
        flex-direction: column;
        > p {
          margin-bottom: 2px;
          color: var(--fett-text-secondary);
        }
        > b {
          font-size: 16px;
          color: var(--fett-text-primary);
        }
        > span {
          font-size: 12px;
          color: var(--fett-text-tertiary);
        }
      }
    }
  }
  > div,
  > section > div {
    > header {
      display: flex;
      align-items: center;
      height: 56px;
      padding: 0 24px;
      margin-bottom: 1px;
      background: var(--fett-bg-panel);
      > h3 {
        margin-bottom: 0;
      }
    }
    > div {
      padding: 14px;
      background: var(--fett-bg-panel);
    }
  }
  > div {
    margin-bottom: 14px;
    > div {
      display: flex;
      flex-direction: column;
      height: 350px;
      padding: 14px;
      > ul {
        flex: 0 0 auto;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 18px;
        margin: 0;
        padding: 0;
        list-style: none;
        > li .ant-badge-status-text {
          font-size: 12px;
          color: var(--fett-text-secondary);
        }
      }
      > section {
        flex: 1 0 0;
      }
    }
  }
  > section {
    display: flex;
    align-items: stretch;
    gap: 14px;
    > div {
      display: flex;
      flex-direction: column;
      > header {
        flex: 0 0 auto;
      }
      > div {
        flex: 1 0 0;
      }
    }
    > div:first-child {
      flex: 1 0 0;
    }
    > div:last-child {
      flex: 0 0 auto;
      width: 380px;
    }
    ul {
      margin: 0;
      padding: 0;
      list-style: none;
      > li {
        display: flex;
        height: 70px;
        padding: 14px;
        background: var(--fett-surface-success-bg);
        border-radius: 8px;
        > .anticon {
          flex: 0 0 auto;
          margin-right: 14px;
          font-size: 32px;
        }
        > div {
          display: flex;
          flex-direction: column;
          font-size: 12px;
          color: var(--fett-text-tertiary);
          > span {
            margin-bottom: 2px;
          }
          > p {
            vertical-align: middle;
            > b {
              margin-right: 6px;
              font-size: 16px;
              color: var(--fett-text-primary);
            }
          }
        }
        &:first-child {
          background: var(--fett-bg-info-tip);
        }
        &:last-child {
          background: var(--fett-surface-warning-bg);
        }
      }
      > li + li {
        margin-top: 14px;
      }
    }
  }
`;

export default QaReply;
